<template>
    <div v-if="isLoading" class="mt-8 flex cursor-default items-center justify-center px-8 py-6">
        <IconLoadingCats/>
    </div>
    <div v-else-if="isEmpty" class="mt-8 flex cursor-default items-center justify-center px-8 py-6">
        <IconEmpty/>
    </div>
    <div v-else class="h-full w-full bg-grey-10 p-3" :class="{'pointer-events-none':isLoading || reportService.exportLoading}">
        <div class="flex w-full items-center justify-between">
            <div class="th-tooltip th-tooltip-bottom-start th-tooltip-dark" :data-tip="$t('list')">
                <button class="th-btn-only-icon th-btn th-btn-plain" @click="goBack">
                    <PtIcon icon="SolidComment" class="th-btn-icon text-2xl" />
                </button>
            </div>

            <div class="flex items-center">
                <button
                    v-if="reportService.downloadPermission"
                    :disabled="disableDownloadBtn"
                    class="th-btn th-btn-outline ml-3 min-w-[130px]"
                    @click="reportService.downloadReport(true)"
                >
                    <img src="@/assets/images/Rectangle.png" class="th-btn-icon"/>
                    <span v-if="reportService.exportLoading && reportService.isPPT || showLoading" class="th-loading th-loading-spinner th-loading-xs"></span>
                    <span v-else>{{ $t('download') }} PPT</span>
                </button>

                <button
                    v-if="reportService.downloadPermission"
                    :disabled="disableDownloadBtn"
                    class="th-btn th-btn-outline ml-3 min-w-[130px]"
                    @click="reportService.downloadReport(false)"
                >
                    <IconWord class="th-btn-icon"></IconWord>
                    <span v-if="reportService.exportLoading && !reportService.isPPT || showLoading" class="th-loading th-loading-spinner th-loading-xs"></span>
                    <span v-else>{{ $t('download') }} Word</span>
                </button>
            </div>

        </div>
        <HeadCard :title="reportService.title" :date="reportService.date"></HeadCard>
        <RiskPointCard :pointList="reportService.riskPointList"></RiskPointCard>
        <RiskScopeCard></RiskScopeCard>
        <PatentListCard :list="reportService.patentList"></PatentListCard>
        <ComparingDocumentCard
            v-for="(compareList, idx) in reportService.docCompareList"
            :key="idx"
            :no="idx + 1"
            :info="compareList.info"
            :list="compareList.list"
            :featureList="compareList.featureList"
        ></ComparingDocumentCard>
        <ResultCard :isNovelty="reportService.isNovelty"></ResultCard>
        <SuggestionCard :companyName="firstCompanyName" @update:Analysis="handleAnalysisUpdate"></SuggestionCard>
        <DisclaimerCard></DisclaimerCard>
    </div>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz'
import { onMounted, computed, ref, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { reportService } from '@/services/report-service';
import HeadCard from '@/components/report/HeadCard.vue'
import RiskPointCard from '@/components/report/RiskPointCard.vue';
import RiskScopeCard from '@/components/report/RiskScopeCard.vue';
import PatentListCard from '@/components/report/PatentListCard.vue';
import ComparingDocumentCard from '@/components/report/ComparingDocumentCard.vue'
import ResultCard from '@/components/report/ResultCard.vue';
import SuggestionCard from '@/components/report/SuggestionCard.vue';
import DisclaimerCard from '@/components/report/DisclaimerCard.vue';
import IconLoadingCats from '@/components/icons/IconLoadingCats.vue';
import IconEmpty from '@/components/icons/IconEmpty.vue';
import IconWord from '@/components/icons/IconWord.vue';
import { hookErrorCode } from '@eureka/services/src/custom-error-handler';
import { EActivityTriggerType } from '@eureka/services/src/types/activity-type';
import { controlDeferred } from '@eureka/utils/control';
import { i18n } from '@/infrastructure/locales';
import '@patsnap-ui/icon/assets/solid/comment.svg'

const router = useRouter();
const isLoading = ref(true);
const isEmpty = ref(false);
const isFreeUser = ref(false);
const LIMIT_DOWNLOAD = 90112032;

function goBack() {
    router.replace({ name: 'result' })
}

onMounted(async() => {
    isLoading.value = true;
    isEmpty.value = true;
    const task = reportService.value.fetchReportData();
    hookErrorCode(task, [LIMIT_DOWNLOAD], (error) => {
        goBack();
        import('@eureka/shared/upgrade-activity').then((mod) => {
            mod.openUpgradeActivity(i18n, { activityTriggerType: EActivityTriggerType.idea, error })
        })
    }).then(() => {
        isEmpty.value = false;
    }).finally(() => {
        isLoading.value = false;
    });

    /**
     * 用户是否可以下载报告
     */
    const { download, freemium } = await controlDeferred?.promise;
    reportService.value.downloadPermission = download;
    isFreeUser.value = freemium;
});

onBeforeUnmount(() => {
    reportService.value.clear();
})

const firstCompanyName = computed(() => {
    return reportService.value?.reportList?.[0]?.org_info?.[0]?.normalized_name || '';
})

/**
 * 免费版用户，不需要等待GPT分析完成
 */
const gptFinish = computed(() => {
    if (isFreeUser.value) return true;
    return reportService.value.allGptFinish;
})

const disableDownloadBtn = computed(() => {
    return !reportService.value.isChartLoaded || isLoading.value || !gptFinish.value || reportService.value.exportLoading;
})

const showLoading = computed(() => {
    return !reportService.value.isChartLoaded || isLoading.value || !gptFinish.value;
})

function handleAnalysisUpdate(data: { type: string, img: string, topRank: string[], name: string }) {
    if (data.type === 'cloud') {
        reportService.value.cloudImg = data.img;
        reportService.value.cloudTopRank = data.topRank.slice(0, 1);
    } else {
        reportService.value.heatImg = data.img;
        reportService.value.heatTopRank = data.topRank.slice(0, 3);
    }
}

</script>
